import {
  ApiAccessControlRepository,
  ApiAccessLogRepository,
  ApiAccessPinRepository,
  ApiAmenityRepository,
  ApiAmenityReservationRepository,
  ApiAnnouncementRepository,
  ApiAuditRepository,
  ApiBillingRepository,
  ApiCfdiRepository,
  ApiChargeRepository,
  ApiDeviceRepository,
  ApiDocumentRepository,
  ApiExpenseRepository,
  ApiCategoryRepository,
  ApiFinanceDashboardRepository,
  ApiFileRepository,
  ApiGuardRepository,
  ApiGuestRepository,
  ApiInventoryRepository,
  ApiLicensePlateRepository,
  ApiLogbookRepository,
  ApiNotificationRepository,
  ApiNotificationConfigRepository,
  ApiNotificationPreferenceRepository,
  MockNotificationConfigRepository,
  MockNotificationPreferenceRepository,
  ApiPaymentGatewayRepository,
  ApiPaymentV2Repository,
  ApiPetRepository,
  ApiPollRepository,
  ApiTransparencyProjectRepository,
  ApiReportRepository,
  ApiRfidCredentialRepository,
  ApiRoleRepository,
  ApiSettingsRepository,
  ApiSupplierRepository,
  ApiTicketRepository,
  ApiUnitRepository,
  ApiUserRepository,
  ApiVehicleRepository,
  ApiWebhookRepository,
  ApiIntegrationCatalogRepository,
  ApiAccessSyncRepository,
  ApiAccessLevelRepository,
  ApiCameraRepository,
  MockCameraRepository,
  MockRoutineRepository,
  MockRoutineExecutionRepository,
  MockRoleRepository,
  ApiRoutineRepository,
  ApiRoutineExecutionRepository,
  ApiDashboardCondominiumRepository,
  ApiCobranzaRepository,
  ApiSectionRepository,
  MockDashboardCondominiumRepository,
  MockCobranzaRepository,
  MockSectionRepository,
  MockAccessLogRepository,
  MockAmenityRepository,
  MockAmenityReservationRepository,
  MockAnnouncementRepository,
  MockTransparencyProjectRepository,
  MockAuditRepository,
  MockBillingRepository,
  MockDeviceRepository,
  MockDocumentRepository,
  MockExpenseRepository,
  MockCategoryRepository,
  MockFinanceDashboardRepository,
  MockGuestRepository,
  MockLicensePlateRepository,
  MockSettingsRepository,
  MockTicketRepository,
  MockUnitRepository,
  MockUserRepository,
  createWebStorageClient,
  initApiClient,
  setOnRateLimitedCallback,
  setOnTokenCompromisedCallback,
  setOnForbiddenCallback,
} from '@/shared/infrastructure/repositories';
import { BLACKLIST_ERROR_CODE } from '@yawi/types';
import {
  IAccessControlRepository,
  IAccessLogRepository,
  IAccessPinRepository,
  IAmenityRepository,
  IAmenityReservationRepository,
  IAnnouncementRepository,
  IAuditRepository,
  IBillingRepository,
  ICfdiRepository,
  IChargeRepository,
  IDeviceRepository,
  IDocumentRepository,
  IExpenseRepository,
  ICategoryRepository,
  IFinanceDashboardRepository,
  IFileRepository,
  IIntegrationCatalogRepository,
  IAccessSyncRepository,
  IAccessLevelRepository,
  ICameraRepository,
  IRoutineRepository,
  IRoutineExecutionRepository,
  IDashboardCondominiumRepository,
  ICobranzaRepository,
  ISectionRepository,
  IGuardRepository,
  IGuestRepository,
  IInventoryRepository,
  ILicensePlateRepository,
  ILogbookRepository,
  INotificationRepository,
  INotificationConfigRepository,
  INotificationPreferenceRepository,
  IPaymentGatewayRepository,
  IPaymentV2Repository,
  IPetRepository,
  IPollRepository,
  ITransparencyProjectRepository,
  IReportRepository,
  IRfidCredentialRepository,
  IRoleRepository,
  ISupplierRepository,
  ITicketRepository,
  IUnitRepository,
  IUserRepository,
  IVehicleRepository,
  IWebhookRepository,
} from '@/shared/types';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useToast } from './ToastContext';

// Default 'api': si falta VITE_API_MODE preferimos fallar con errores de red antes que
// servir datos inventados que parecen reales. Mock sigue disponible, pero explícito.
const API_MODE = import.meta.env.VITE_API_MODE || 'api';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// settingsRepo uses the concrete type so consumers can call the full SystemSettings
// API (getSettings, updateSettings, updateBrandingConfig, etc.) without casts.
// MockSettingsRepository is cast to ApiSettingsRepository in mock mode — it covers
// the common methods used in development; any missing method would surface at runtime.
interface DIContextType {
  userRepo: IUserRepository;
  guestRepo: IGuestRepository;
  accessLogRepo: IAccessLogRepository;
  accessControlRepo: IAccessControlRepository;
  accessPinRepo: IAccessPinRepository;
  unitRepo: IUnitRepository;
  amenityRepo: IAmenityRepository;
  amenityReservationRepo: IAmenityReservationRepository;
  chargeRepo: IChargeRepository;
  paymentRepo: IPaymentV2Repository;
  expenseRepo: IExpenseRepository;
  categoryRepo: ICategoryRepository;
  financeDashboardRepo: IFinanceDashboardRepository;
  deviceRepo: IDeviceRepository;
  announcementRepo: IAnnouncementRepository;
  ticketRepo: ITicketRepository;
  settingsRepo: ApiSettingsRepository;
  licensePlateRepo: ILicensePlateRepository;
  auditRepo: IAuditRepository;
  documentRepo: IDocumentRepository;
  petRepo: IPetRepository;
  vehicleRepo: IVehicleRepository;
  notificationRepo: INotificationRepository;
  pollRepo: IPollRepository;
  transparencyProjectRepo: ITransparencyProjectRepository;
  webhookRepo: IWebhookRepository;
  paymentGatewayRepo: IPaymentGatewayRepository;
  cfdiRepo: ICfdiRepository;
  fileRepo: IFileRepository;
  rfidCredentialRepo: IRfidCredentialRepository;
  billingRepo: IBillingRepository;
  reportRepo: IReportRepository;
  guardRepo: IGuardRepository;
  logbookRepo: ILogbookRepository;
  supplierRepo: ISupplierRepository;
  inventoryRepo: IInventoryRepository;
  integrationCatalogRepo: IIntegrationCatalogRepository;
  accessSyncRepo: IAccessSyncRepository;
  accessLevelRepo: IAccessLevelRepository;
  roleRepo: IRoleRepository;
  cameraRepo: ICameraRepository;
  notificationConfigRepo: INotificationConfigRepository;
  notificationPreferenceRepo: INotificationPreferenceRepository;
  routineRepo: IRoutineRepository;
  routineExecutionRepo: IRoutineExecutionRepository;
  dashboardCondominiumRepo: IDashboardCondominiumRepository;
  cobranzaRepo: ICobranzaRepository;
  sectionRepo: ISectionRepository;
  apiMode: 'mock' | 'api';
  /** Register the auth-layer's unauthorized handler (called on 401 / token compromise). */
  setOnUnauthorizedHandler: (fn: () => void) => void;
  /** Register the auth-layer's forbidden handler (called on 403 — permiso denegado a mitad de sesión). */
  setOnForbiddenHandler: (fn: (message: string, method: string) => void) => void;
  /** Register the Listado Negro handler (called solo en 403 UNIT_BLACKLISTED). */
  setOnBlacklistedHandler: (fn: () => void) => void;
}

const createRepositories = (
  mode: 'mock' | 'api',
  onUnauthorized: () => void
): Omit<
  DIContextType,
  'apiMode' | 'setOnUnauthorizedHandler' | 'setOnForbiddenHandler' | 'setOnBlacklistedHandler'
> => {
  // fileRepo siempre usa ApiFileRepository (no hay MockFileRepository), así que
  // el cliente API debe inicializarse en ambos modos, no solo en 'api'.
  initApiClient({
    baseUrl: API_BASE_URL,
    storageClient: createWebStorageClient(),
    onUnauthorized,
  });

  if (mode === 'api') {
    return {
      userRepo: new ApiUserRepository(),
      guestRepo: new ApiGuestRepository(),
      accessLogRepo: new ApiAccessLogRepository(),
      accessControlRepo: new ApiAccessControlRepository(),
      accessPinRepo: new ApiAccessPinRepository(),
      unitRepo: new ApiUnitRepository(),
      amenityRepo: new ApiAmenityRepository(),
      amenityReservationRepo: new ApiAmenityReservationRepository(),
      chargeRepo: new ApiChargeRepository(),
      paymentRepo: new ApiPaymentV2Repository(),
      expenseRepo: new ApiExpenseRepository(),
      categoryRepo: new ApiCategoryRepository(),
      financeDashboardRepo: new ApiFinanceDashboardRepository(),
      deviceRepo: new ApiDeviceRepository(),
      announcementRepo: new ApiAnnouncementRepository(),
      ticketRepo: new ApiTicketRepository(),
      settingsRepo: new ApiSettingsRepository(),
      licensePlateRepo: new ApiLicensePlateRepository(),
      auditRepo: new ApiAuditRepository(),
      documentRepo: new ApiDocumentRepository(),
      petRepo: new ApiPetRepository(),
      vehicleRepo: new ApiVehicleRepository(),
      notificationRepo: new ApiNotificationRepository(),
      pollRepo: new ApiPollRepository(),
      transparencyProjectRepo: new ApiTransparencyProjectRepository(),
      webhookRepo: new ApiWebhookRepository(),
      paymentGatewayRepo: new ApiPaymentGatewayRepository(),
      cfdiRepo: new ApiCfdiRepository(),
      fileRepo: new ApiFileRepository(),
      rfidCredentialRepo: new ApiRfidCredentialRepository(),
      billingRepo: new ApiBillingRepository(),
      reportRepo: new ApiReportRepository(),
      guardRepo: new ApiGuardRepository(),
      logbookRepo: new ApiLogbookRepository(),
      supplierRepo: new ApiSupplierRepository(),
      inventoryRepo: new ApiInventoryRepository(),
      integrationCatalogRepo: new ApiIntegrationCatalogRepository(),
      accessSyncRepo: new ApiAccessSyncRepository(),
      accessLevelRepo: new ApiAccessLevelRepository(),
      roleRepo: new ApiRoleRepository(),
      cameraRepo: new ApiCameraRepository(),
      notificationConfigRepo: new ApiNotificationConfigRepository(),
      notificationPreferenceRepo: new ApiNotificationPreferenceRepository(),
      routineRepo: new ApiRoutineRepository(),
      routineExecutionRepo: new ApiRoutineExecutionRepository(),
      dashboardCondominiumRepo: new ApiDashboardCondominiumRepository(),
      cobranzaRepo: new ApiCobranzaRepository(),
      sectionRepo: new ApiSectionRepository(),
    };
  }

  return {
    userRepo: new MockUserRepository(),
    guestRepo: new MockGuestRepository(),
    accessLogRepo: new MockAccessLogRepository(),
    accessControlRepo: new ApiAccessControlRepository(),
    accessPinRepo: new ApiAccessPinRepository(),
    unitRepo: new MockUnitRepository(),
    amenityRepo: new MockAmenityRepository(),
    amenityReservationRepo: new MockAmenityReservationRepository(),
    chargeRepo: new ApiChargeRepository(),
    paymentRepo: new ApiPaymentV2Repository(),
    expenseRepo: new MockExpenseRepository(),
    categoryRepo: new MockCategoryRepository(),
    financeDashboardRepo: new MockFinanceDashboardRepository(),
    deviceRepo: new MockDeviceRepository(),
    announcementRepo: new MockAnnouncementRepository(),
    ticketRepo: new MockTicketRepository(),
    settingsRepo: new MockSettingsRepository() as unknown as ApiSettingsRepository,
    licensePlateRepo: new MockLicensePlateRepository(),
    auditRepo: new MockAuditRepository(),
    documentRepo: new MockDocumentRepository(),
    petRepo: new ApiPetRepository(),
    vehicleRepo: new ApiVehicleRepository(),
    notificationRepo: new ApiNotificationRepository(),
    pollRepo: new ApiPollRepository(),
    transparencyProjectRepo: new MockTransparencyProjectRepository(),
    webhookRepo: new ApiWebhookRepository(),
    paymentGatewayRepo: new ApiPaymentGatewayRepository(),
    cfdiRepo: new ApiCfdiRepository(),
    fileRepo: new ApiFileRepository(),
    rfidCredentialRepo: new ApiRfidCredentialRepository(),
    billingRepo: new MockBillingRepository(),
    reportRepo: new ApiReportRepository(),
    guardRepo: new ApiGuardRepository(),
    logbookRepo: new ApiLogbookRepository(),
    supplierRepo: new ApiSupplierRepository(),
    inventoryRepo: new ApiInventoryRepository(),
    integrationCatalogRepo: new ApiIntegrationCatalogRepository(),
    accessSyncRepo: new ApiAccessSyncRepository(),
    accessLevelRepo: new ApiAccessLevelRepository(),
    roleRepo: new MockRoleRepository(),
    cameraRepo: new MockCameraRepository(),
    notificationConfigRepo: new MockNotificationConfigRepository(),
    notificationPreferenceRepo: new MockNotificationPreferenceRepository(),
    routineRepo: new MockRoutineRepository(),
    routineExecutionRepo: new MockRoutineExecutionRepository(),
    dashboardCondominiumRepo: new MockDashboardCondominiumRepository(),
    cobranzaRepo: new MockCobranzaRepository(),
    sectionRepo: new MockSectionRepository(),
  };
};

const DIContext = createContext<DIContextType | null>(null);

export const DIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toast = useToast();
  const onUnauthorizedRef = useRef<(() => void) | null>(null);
  const onForbiddenRef = useRef<((message: string, method: string) => void) | null>(null);
  const onBlacklistedRef = useRef<(() => void) | null>(null);

  // Repositories are created once — the factory closure delegates unauthorized
  // calls to onUnauthorizedRef, which AuthProvider sets after it mounts.
  const [repos] = useState<
    Omit<
      DIContextType,
      'apiMode' | 'setOnUnauthorizedHandler' | 'setOnForbiddenHandler' | 'setOnBlacklistedHandler'
    >
  >(
    () => createRepositories(API_MODE as 'mock' | 'api', () => onUnauthorizedRef.current?.())
  );

  useEffect(() => {
    if (API_MODE === 'api') {
      setOnRateLimitedCallback((retryAfter) => {
        const seconds = retryAfter || 60;
        toast.warning(`Demasiados intentos. Espera ${seconds} segundos.`, 'Límite alcanzado');
      });
      setOnTokenCompromisedCallback(() => {
        onUnauthorizedRef.current?.();
        toast.error(
          'Tu sesión fue cerrada por seguridad. Por favor inicia sesión nuevamente.',
          'Sesión comprometida'
        );
      });
      // 403: el token sigue siendo válido pero el backend negó el permiso — puede ser
      // que un admin haya cambiado el rol/permisos de este usuario a mitad de sesión.
      // Delegamos a AuthProvider para que resincronice currentUser (y con eso, las
      // habilidades CASL) en vez de forzar logout.
      setOnForbiddenCallback((message, method, code) => {
        // Listado Negro: el backend ya confirmó el bloqueo total, no hay nada
        // que resincronizar — se cierra la app al usuario de inmediato.
        if (code === BLACKLIST_ERROR_CODE) {
          onBlacklistedRef.current?.();
          return;
        }
        onForbiddenRef.current?.(message, method);
      });
    }
  }, [toast]);

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(`🔧 Hommi running in ${API_MODE.toUpperCase()} mode`);
      if (API_MODE === 'api') console.log(`📡 API URL: ${API_BASE_URL}`);
    }
  }, []);

  const setOnUnauthorizedHandler = useCallback((fn: () => void) => {
    onUnauthorizedRef.current = fn;
  }, []);

  const setOnBlacklistedHandler = useCallback((fn: () => void) => {
    onBlacklistedRef.current = fn;
  }, []);

  const setOnForbiddenHandler = useCallback((fn: (message: string, method: string) => void) => {
    onForbiddenRef.current = fn;
  }, []);

  return (
    <DIContext.Provider
      value={{
        ...repos,
        apiMode: API_MODE as 'mock' | 'api',
        setOnUnauthorizedHandler,
        setOnForbiddenHandler,
        setOnBlacklistedHandler,
      }}
    >
      {children}
    </DIContext.Provider>
  );
};

export const useDI = (): DIContextType => {
  const ctx = useContext(DIContext);
  if (!ctx) throw new Error('useDI must be used within a DIProvider');
  return ctx;
};
